import { founderTypes } from '../data/founderTypes';
import { scoringConfig } from '../data/scoringConfig';
import { calculateArchetypes } from './archetypeCalculator';
import { addResultsRun } from './resultsHistoryStorage';
import {
  isLocalStorageAvailable,
  safeLocalStorageGet,
  safeLocalStorageRemove,
  safeLocalStorageSet,
} from './storageAvailability';

// Calculate the profile based on the user's answers
export const calculateProfile = (answers) => {
  const questionWeights = scoringConfig?.questionWeights || {};
  const tieBreakOrder = scoringConfig?.tieBreakOrder || [];

  const getAnswerWeight = (answer) => {
    const qid = answer?.questionId;
    const weight = qid && questionWeights[qid] ? Number(questionWeights[qid]) : 1;
    return Number.isFinite(weight) && weight > 0 ? weight : 1;
  };

  // Weighted counts per type
  const typeCounts = answers.reduce((counts, answer) => {
    const type = answer?.type;
    if (!type) return counts;
    const weight = getAnswerWeight(answer);
    counts[type] = (counts[type] || 0) + weight;
    return counts;
  }, {});

  const tieBreakIndex = (type) => {
    const idx = tieBreakOrder.indexOf(type);
    return idx === -1 ? 999 : idx;
  };

  // Sort types by weighted score, then tie break order
  const sortedTypes = Object.keys(typeCounts).sort((a, b) => {
    const diff = (typeCounts[b] || 0) - (typeCounts[a] || 0);
    if (diff !== 0) return diff;
    return tieBreakIndex(a) - tieBreakIndex(b);
  });

  // Get primary and secondary types
  const primaryType = sortedTypes[0] || null;
  const secondaryType = sortedTypes[1] || null;

  // Calculate percentages for each type (weighted)
  const totalAnswers = answers.length;
  const totalScore = Object.values(typeCounts).reduce((sum, v) => sum + (Number(v) || 0), 0);
  const percentages = {};
  
  Object.keys(typeCounts).forEach(type => {
    percentages[type] = totalScore > 0 ? Math.round((typeCounts[type] / totalScore) * 100) : 0;
  });
  
  // Ensure all founder types have a percentage (even if 0)
  Object.keys(founderTypes).forEach(type => {
    if (!percentages[type]) {
      percentages[type] = 0;
    }
  });

  // Confidence (simple, explainable): margin between top two scores
  const topScore = primaryType ? (typeCounts[primaryType] || 0) : 0;
  const secondScore = secondaryType ? (typeCounts[secondaryType] || 0) : 0;
  const margin = Math.max(0, topScore - secondScore);
  const marginPercent = totalScore > 0 ? Math.round((margin / totalScore) * 100) : 0;
  const confidenceLabel = marginPercent >= 18 ? 'high' : marginPercent >= 10 ? 'medium' : 'low';

  // Drivers for transparency (sorted by weight, then stable)
  const drivers = answers
    .map((a, idx) => ({
      questionId: a?.questionId || null,
      questionText: a?.questionText || null,
      optionId: a?.optionId || null,
      optionText: a?.optionText || null,
      type: a?.type || null,
      weight: getAnswerWeight(a),
      idx,
    }))
    .filter(d => d.type)
    .sort((a, b) => (b.weight - a.weight) || (a.idx - b.idx));

  // Get compatibility information
  let compatibleTypes = [];
  if (primaryType && founderTypes[primaryType]) {
    compatibleTypes = founderTypes[primaryType].compatibleWith || [];
  }

  const archetypeResult = calculateArchetypes(percentages);

  return {
    primaryType,
    secondaryType,
    typeCounts,
    percentages,
    compatibleTypes,
    totalAnswers,
    scoringVersion: scoringConfig?.version || 1,
    confidence: {
      label: confidenceLabel,
      marginPercent,
    },
    drivers,
    archetypes: archetypeResult,
  };
};

/** Top weighted answers that shaped the profile (for transparency UI). */
export const getResultDrivers = (profile, { limit = 5, typeFilter = null } = {}) => {
  const drivers = Array.isArray(profile?.drivers) ? profile.drivers : [];
  const filtered = typeFilter ? drivers.filter((d) => d.type === typeFilter) : drivers;
  return filtered.slice(0, limit);
};

// Save profile results (and optional answer evidence) to localStorage
export const saveResults = (profile, answers = []) => {
  const payload = {
    profile,
    answers,
    createdAt: new Date().toISOString(),
    schemaVersion: 3,
  };

  if (!safeLocalStorageSet('founderDnaResults', JSON.stringify(payload))) {
    throw new Error('Could not save results. Browser storage may be blocked or full.');
  }

  // Also append to local run history for progress tracking
  try {
    addResultsRun({
      profile,
      answers,
      createdAt: payload.createdAt,
      schemaVersion: payload.schemaVersion,
    });
  } catch (e) {
    // Non blocking
    console.warn('Failed to store results history:', e);
  }
};

// Get profile results from localStorage
export const getResults = () => {
  const resultsStr = safeLocalStorageGet('founderDnaResults');
  if (!resultsStr) return null;

  try {
    const parsed = JSON.parse(resultsStr);

    // Backward compatibility (v1 stored the profile object directly)
    if (parsed && parsed.primaryType) {
      return { profile: parsed, answers: [], createdAt: null, schemaVersion: 1 };
    }

    // v2 schema
    if (parsed && parsed.profile) {
      return {
        profile: parsed.profile,
        answers: Array.isArray(parsed.answers) ? parsed.answers : [],
        createdAt: parsed.createdAt || null,
        schemaVersion: parsed.schemaVersion || 2,
      };
    }

    return null;
  } catch (e) {
    console.warn('Failed to parse stored results:', e);
    return null;
  }
};

// Clear profile results from localStorage
export const clearResults = () => {
  safeLocalStorageRemove('founderDnaResults');
};

export { isLocalStorageAvailable };